import { request } from './client'
import type { DurableProgress } from '../stores/kidProgress'
import type { StaffAuth } from './teaching'
import type { KidSession } from './auth'

/**
 * What a teacher needs to get a class signing in — OD-52's other half.
 *
 * 🔴 `play-credentials.controller.ts` IS AN ENTIRE CONTROLLER NOTHING CALLED.
 * Three routes on `classes/:classId/play-*` plus `PATCH
 * /players/:playerId/play-password`, built, scoped to TEACHER and ADMIN, and
 * tested — with no screen on the other end. So the class code a child types
 * existed only in the dev seed script's console output, and a real teacher had
 * no way to find one. The child's door was finished and the door's key was
 * unreachable, which is this repo's own recurring failure with the two halves
 * swapped.
 *
 * ⚠️ EVERY CALL IS SCOPED SERVER-SIDE TO THE CALLER'S OWN CLASSES. `ScopeService`
 * decides; nothing here filters. A teacher naming another section's `classId`
 * gets a 403, and that is the control.
 */

/**
 * `GET /classes/:classId/play-code`.
 *
 * 🔴 A GET THAT MINTS ON FIRST ASK, AND STABLE FOREVER AFTER. The code is not
 * chosen by anybody: it is generated from an alphabet with no `O`/`0`, `I`/`1`,
 * `L` or `S`/`5` in it, because a five-year-old reads it off a whiteboard and
 * every one of those pairs is the same glyph to them. A teacher reopening this
 * screen must see the code already on their board, so this is idempotent after
 * the first call.
 */
export const getClassPlayCode = (
  classId: string,
  auth: StaffAuth = {},
): Promise<{ classId: string; code: string }> =>
  request(`/classes/${encodeURIComponent(classId)}/play-code`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * `POST /classes/:classId/play-code/rotate` — a new code; the old one stops
 * working immediately.
 *
 * ⚠️ A POST BECAUSE IT IS NOT IDEMPOTENT, and the screen in front of it must
 * confirm. Rotating mid-morning strands every tablet in the room until somebody
 * writes the new code up.
 */
export const rotateClassPlayCode = (
  classId: string,
  auth: StaffAuth = {},
): Promise<{ classId: string; code: string }> =>
  request(`/classes/${encodeURIComponent(classId)}/play-code/rotate`, {
    method: 'POST',
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * One child's readiness to sign in.
 *
 * 🔴 `hasPassword`, NEVER A PASSWORD. The column holds an argon2 hash, so there
 * is nothing to return — and the absence of a "show password" route is a
 * property of the design rather than a missing feature.
 *
 * ⚠️ IT IS ALSO WHICH DOOR THIS CHILD USES. `kid-auth.service.ts` accepts a
 * child's own name in place of a password for a child who has none, so
 * `hasPassword: false` reads as *"signs in with their name"* rather than as
 * *"cannot sign in"* — which is what it meant before that door existed, and what
 * a screen written against the old meaning would still say.
 */
export interface PlayAccessRow {
  playerId: string
  name: string
  rollNumber: string | null
  hasPassword: boolean
}

/** `GET /classes/:classId/play-access` — who in this class can sign in yet. */
export const getPlayAccess = (
  classId: string,
  auth: StaffAuth = {},
): Promise<PlayAccessRow[]> =>
  request(`/classes/${encodeURIComponent(classId)}/play-access`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * `PATCH /players/:playerId/play-password`.
 *
 * ⚠️ `null` IS THE CLEAR, AND IT IS THE ONLY WAY BACK. Clearing returns the
 * child to the name door; without it a password, once set, could only ever be
 * changed — so a child who has left, or a password written on a desk, could not
 * be switched off short of a database edit.
 */
export const setPlayPassword = (
  playerId: string,
  password: string | null,
  auth: StaffAuth = {},
): Promise<{ playerId: string; hasPassword: boolean }> =>
  request(`/players/${encodeURIComponent(playerId)}/play-password`, {
    method: 'PATCH',
    body: { password },
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * `POST /players/:playerId/play-session` — Teacher Play Mode.
 *
 * 🔴 IT RETURNS EXACTLY WHAT `POST /auth/kid` RETURNS, AND THAT IS THE WHOLE
 * DESIGN. The screen hands it to `setKidSession()` and `KidApp` plays as that
 * child with nothing else changed anywhere: the outbox already prefers a
 * signed-in child's token over the tablet's at flush time (`device.ts`), so
 * every event and the progress snapshot are written under the child's own
 * credential. *"Progress saves to roll 03"* is true because the token names
 * roll 03 — not because a screen remembered to say so.
 *
 * 🔴 THE TEACHER'S TOKEN AUTHORISES THE MINT AND THEN PLAYS NO FURTHER PART.
 * `context: 'user'` here; every write that follows is `context: 'device'` under
 * the returned token. The old comment on D3's disabled button feared *"changing
 * the gameplay write path"* — this changes none of it.
 *
 * ⚠️ THE `deviceId` IS THIS TABLET'S, READ FROM `device.ts`. It is what the
 * `Device` row is keyed by, so it is what an admin revokes by — a session minted
 * against a device nobody recorded would be one S2 cannot switch off.
 */
/** An open practice a teacher assigned to this child (P3-28) — enumerated codes, never prose. */
export interface KidAssignment {
  id: string
  game: string
  level: number | null
  skill: string | null
  focusTag: string | null
}

/**
 * What the play runtime must enforce for this child, sent with the launch.
 *
 * ⚠️ READ BY THE TEST PAGE ONLY FOR NOW (`frontend/test-pages/play-runtime.html`).
 * `KidApp` keeps ignoring it until the user moves the timer / camera-off /
 * assignment pieces into the real flow.
 */
export interface PlaySettings {
  /** T3 "The session ends by itself after N minutes" (`School.sessionMinutes`); null = no limit. */
  sessionMinutes: number | null
  /** M9: the parent switched the camera off — play this child in tap/keyboard mode only (D1). */
  cameraOptOut: boolean
  assignments: KidAssignment[]
}

/** `POST /players/:id/play-session` — `POST /auth/kid`'s session plus the runtime settings. */
export type TeacherPlaySession = KidSession & { play: PlaySettings }

export const startPlaySession = (
  playerId: string,
  deviceId: string,
  auth: StaffAuth = {},
): Promise<TeacherPlaySession> =>
  request(`/players/${encodeURIComponent(playerId)}/play-session`, {
    method: 'POST',
    body: { deviceId },
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

/**
 * `GET /progress/:playerId` under the CHILD's own device token.
 *
 * 🔴 IT EXISTS SO A LAUNCH CAN SEED THE MIRROR BEFORE THE FIRST ROUND ENDS.
 * `student.ts` already reads this route, but under a STAFF token — the tier that
 * shows a teacher a child's card. This is the same route read as the child, in
 * the two seconds between minting their session and handing over the tablet,
 * because `POST /progress/:playerId` upserts a snapshot: whatever the device
 * believes the child owns is what the child then owns.
 *
 * ⚠️ THE CALLER MUST TREAT A FAILURE AS "NO ANSWER", NEVER AS AN ERROR TO
 * SURFACE. *"Gameplay never awaits the network"* (offline-sync.md §1); a child
 * standing at a tablet is not a place to report a fetch.
 */
export const getChildProgress = (
  playerId: string,
  deviceToken: string,
  signal?: AbortSignal,
): Promise<DurableProgress> =>
  request<DurableProgress>(`/progress/${encodeURIComponent(playerId)}`, {
    context: 'device',
    token: deviceToken,
    signal,
  })

/**
 * `KALQ8R2M` → `KALQ-8R2M`.
 *
 * 🔴 GROUPED FOR THE PERSON COPYING IT ONTO A WHITEBOARD, and the server accepts
 * the grouping back — `kid-auth.dto.ts` strips spaces and dashes before it looks
 * anything up, so what a teacher writes and what a child types need not match
 * character for character. Eight unbroken characters is the shape people
 * transcribe wrong.
 *
 * ⚠️ IT IS DISPLAY ONLY. The value sent anywhere is the raw code.
 */
export function groupCode(code: string): string {
  const clean = code.replace(/[\s-]/g, '').toUpperCase()
  if (clean.length < 6) return clean
  const half = Math.ceil(clean.length / 2)
  return `${clean.slice(0, half)}-${clean.slice(half)}`
}
